import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { ArrowUpRight, Coins, MessageCircle } from "lucide-react";
import { useSocket } from "../context/SocketContext";

/**
 * Bid-aukció overlay, a .chat cellában (lásd RightPanel.jsx), pontosan úgy
 * mint a Karma/RainOverlay/BarkobaOverlay: amíg aktív, lefedi a Twitch
 * chatet, amikor nincs licit, visszaadja a helyet.
 *
 * A backend (websocket-bidauction, utils/games/bidAuction.js) minden
 * bid_auction:* eseményt a KÖZÖS "overlay" socket csatornán küldi (nincs
 * saját csatornája, mint a rain:event-nek) -- ugyanaz a minta, mint a
 * barkoba:* eseményeknél, ezért ez a komponens is a WordGame.tsx
 * (BarkobaOverlay) szerkezetét követi, nem a Rain-ét.
 *
 * A háromféle forrás (induló REST hydration, socket "overlay:hydrate"
 * reconnectkor, és a kézzel küldött bid_auction:* eventek) MIND a
 * bidAuction.js publicState()-jének ugyanazt az alakját adja -- egy teljes
 * pillanatkép, nem részleges diff -- ezért egyetlen applyState()
 * elegendő mindhárom bemenethez.
 */

/** A nyertes-képernyő ennyi ideig látszik, mielőtt a szerver reset-et küld
 * -- ugyanaz az érték mint a backend WINNER_HOLD_MS-je, csak a kilépő
 * animáció időzítéséhez kell, a tényleges elrejtést a szerver vezérli. */
const EXIT_MS = 850;
const HISTORY_ROWS = 5;

type BidAuctionPhase = "idle" | "open" | "settled";

interface BidAuctionHistoryRow {
  name: string;
  amount: number;
}

interface BidAuctionState {
  phase: BidAuctionPhase;
  currentAmount: number;
  minIncrement: number;
  nextMinAmount: number;
  nextMaxAmount: number;
  leaderName: string | null;
  history: BidAuctionHistoryRow[];
  phaseStartedAt: number;
  phaseDurationMs: number;
  winnerName: string | null;
  winnerAmount: number | null;
  bidMin: number;
  bidMax: number;
}

const IDLE_STATE: BidAuctionState = {
  phase: "idle",
  currentAmount: 0,
  minIncrement: 0,
  nextMinAmount: 0,
  nextMaxAmount: 0,
  leaderName: null,
  history: [],
  phaseStartedAt: 0,
  phaseDurationMs: 0,
  winnerName: null,
  winnerAmount: null,
  bidMin: 0,
  bidMax: 0,
};

function fmt(n: number): string {
  return n.toLocaleString();
}

function fmtTime(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? "0" : ""}${r}`;
}

const EspontIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <img src="/espont.png" alt="" className={`${className} object-contain`} />
);

/** Visszaszámláló a fázis kezdetéből, 250ms-enként frissül -- ugyanaz a
 * mintázat mint a CardGame.tsx useCountdown()-ja. */
function useCountdown(startedAt: number, durationMs: number, active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    let alive = true;
    const tick = () => {
      if (!alive) return;
      setNow(Date.now());
      raf.current = window.setTimeout(tick, 250) as unknown as number;
    };
    tick();
    return () => {
      alive = false;
      if (raf.current) window.clearTimeout(raf.current);
    };
  }, [active, startedAt, durationMs]);

  const remainingMs = Math.max(0, startedAt + durationMs - now);
  return remainingMs / 1000;
}

interface ConfettoSpec {
  x: number;
  y: number;
  r: number;
  d: number;
  gold: boolean;
}

function spawnConfetti(seed: number): ConfettoSpec[] {
  const rand = (n: number) => {
    // kis determinisztikus PRNG a seedből, hogy ugyanaz a render ugyanazt adja
    const x = Math.sin(seed * 999 + n * 37.17) * 10000;
    return x - Math.floor(x);
  };
  return Array.from({ length: 16 }, (_, i) => {
    const angle = rand(i) * Math.PI * 2;
    const dist = 40 + rand(i + 0.5) * 70;
    return {
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist - 20,
      r: rand(i + 0.25) * 360,
      d: rand(i + 0.75) * 150,
      gold: i % 2 === 1,
    };
  });
}

export function BidAuction() {
  const { socket } = useSocket();
  const [state, setState] = useState<BidAuctionState>(IDLE_STATE);
  const [visible, setVisible] = useState(false);
  const [settleKey, setSettleKey] = useState(0);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevPhaseRef = useRef<BidAuctionPhase>("idle");
  const panelRef = useRef<HTMLDivElement | null>(null);
  const toprowRef = useRef<HTMLDivElement | null>(null);
  const amountRef = useRef<HTMLDivElement | null>(null);
  const prevAmountRef = useRef<number | null>(null);
  const prevPhaseForPulseRef = useRef<BidAuctionPhase>("idle");

  const applyState = (data: Partial<BidAuctionState> | null | undefined) => {
    if (!data || data.phase === "idle" || !data.phase) {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      setVisible(false);
      setTimeout(() => setState(IDLE_STATE), EXIT_MS);
      return;
    }
    setState((prev) => ({ ...prev, ...data } as BidAuctionState));
    setVisible(true);
  };

  // Induló REST hydration -- az első mountnál, mielőtt bármilyen socket
  // event jönne. Ugyanaz a végpont (GET /bid-auction/overlay) amit a
  // backend erre a célra épített, publikus, nem kell token.
  useEffect(() => {
    let cancelled = false;
    fetch("https://api.palinagyonbuzi.com/api/bid-auction/overlay")
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) applyState(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Socket-alapú hydration -- minden connect/reconnect eseménynél lefut
  // (server.js io.on("connection", ...) már tartalmazza a bidAuction mezőt).
  useEffect(() => {
    if (!socket) return;
    const handleHydrate = (data: { bidAuction?: BidAuctionState }) => {
      if (data.bidAuction) applyState(data.bidAuction);
    };
    const s = socket as any;
    s.on("overlay:hydrate", handleHydrate);
    return () => {
      s.off("overlay:hydrate", handleHydrate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // A tényleges bid_auction:* események -- a KÖZÖS "overlay" csatornán jönnek,
  // ugyanúgy mint a barkoba:*, nincs saját csatornájuk.
  useEffect(() => {
    if (!socket) return;
    const onOverlay = (msg: { type: string; data: any }) => {
      if (!msg || typeof msg.type !== "string") return;
      if (!msg.type.startsWith("bid_auction:")) return;
      if (msg.type === "bid_auction:reset") {
        applyState(null);
        return;
      }
      applyState(msg.data);
    };
    const s = socket as any;
    s.on("overlay", onOverlay);
    return () => {
      s.off("overlay", onOverlay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // Konfetti + kalapács-csapás csak ÚJ "settled" belépésnél indul, nem minden
  // renderelésnél. A panel-rázkódás (bp-impact) class-t manuálisan kell
  // visszaadni egy force-reflow-val, mert ugyanaz a .bidauction-panel elem
  // marad mounted (nem lehet key-jel remountolni anélkül hogy a konfetti/
  // kalapács gyerekei is elveszítenék a saját state-jüket) -- lásd a
  // CSS .bp-impact definícióját.
  useEffect(() => {
    if (state.phase === "settled" && prevPhaseRef.current !== "settled") {
      setSettleKey((k) => k + 1);
      const el = panelRef.current;
      if (el) {
        el.classList.remove("bp-impact");
        void el.offsetWidth;
        el.classList.add("bp-impact");
      }
    }
    prevPhaseRef.current = state.phase;
  }, [state.phase]);

  // Pulzálás a vezető-sávon és az összegen, amikor egy MÁR NYITOTT aukción
  // belül érkezik új bid (nem az aukció indulásakor -- a wasOpen guard ezt
  // zárja ki, különben a startAmount is "változásnak" tűnne az előző kör
  // végső összegéhez képest).
  useEffect(() => {
    const wasOpen = prevPhaseForPulseRef.current === "open";
    if (
      state.phase === "open" &&
      wasOpen &&
      prevAmountRef.current !== null &&
      prevAmountRef.current !== state.currentAmount
    ) {
      const tr = toprowRef.current;
      const am = amountRef.current;
      if (tr) {
        tr.classList.remove("pulse");
        void tr.offsetWidth;
        tr.classList.add("pulse");
      }
      if (am) {
        am.classList.remove("pulse-num");
        void am.offsetWidth;
        am.classList.add("pulse-num");
      }
    }
    prevAmountRef.current = state.currentAmount;
    prevPhaseForPulseRef.current = state.phase;
  }, [state.currentAmount, state.phase]);

  useEffect(
    () => () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    },
    [],
  );

  // A szerver csak az ELSO bidnel allitja be phaseStartedAt-ot (lasd
  // bidAuction.js) -- addig 0 marad, azaz "nyitva, de meg nem ketyeg az ora".
  const notStartedYet = state.phase === "open" && state.phaseStartedAt === 0;
  const remainingSeconds = useCountdown(
    state.phaseStartedAt,
    state.phaseDurationMs,
    visible && state.phase === "open" && !notStartedYet,
  );
  const totalSeconds = state.phaseDurationMs / 1000 || 1;
  const frac =
    state.phase === "open"
      ? notStartedYet
        ? 1
        : Math.max(0, remainingSeconds / totalSeconds)
      : 0;
  const danger = state.phase === "open" && !notStartedYet && remainingSeconds <= 5;
  const clockSeconds = notStartedYet ? totalSeconds : remainingSeconds;

  const historyRows = useMemo(
    () => state.history.slice(1, 1 + HISTORY_ROWS),
    [state.history],
  );
  const confetti = useMemo(() => spawnConfetti(settleKey), [settleKey]);

  if (!visible && state.phase === "idle") return null;

  const isSettled = state.phase === "settled";

  return (
    <div className={`bidauction-root${visible ? "" : " bidauction-exit"}`}>
      <style>{CSS}</style>

      <div className="bidauction-panel" ref={panelRef}>
        <div className="bp-timebar-track">
          <div
            className={`bp-timebar-fill${danger ? " danger" : ""}`}
            style={{ width: `${(frac * 100).toFixed(1)}%` }}
          />
        </div>

        <div className="bp-label">
          <div className="bp-label-left">
            <span className="bp-dot" />
            Live Bid
          </div>
          {state.phase === "open" && (
            <span className={`bp-clock${danger ? " danger" : ""}`}>
              {fmtTime(clockSeconds)}
            </span>
          )}
        </div>

        <div className="bp-toprow" ref={toprowRef}>
          <div className="bp-bidder">
            <div className="bp-bidder-name">{state.leaderName ?? "–"}</div>
            <div className="bp-bidder-tag">on the lead</div>
          </div>
          <div className="bp-amount" ref={amountRef}>
            <span>{fmt(state.currentAmount)}</span>
            <EspontIcon className="bp-amount-icon" />
          </div>
        </div>

        {state.phase === "open" && (
          <div className="bp-next">
            <span className="bp-col bp-next-amount">
              <ArrowUpRight className="bp-next-icon" />
              Next: <b>{fmt(state.nextMinAmount)}</b>
            </span>
            <span className="bp-col bp-howto-wrap">
              <span className="bp-howto-badge">
                <MessageCircle className="bp-howto-icon" />
                !bid <b>{fmt(state.nextMinAmount)}</b> in chat
              </span>
            </span>
            {state.bidMax > 0 && (
              <span className="bp-col bp-range">
                <Coins className="bp-range-icon" />
                Bid: <b>{fmt(state.nextMinAmount)}-{fmt(state.nextMaxAmount)}</b>
              </span>
            )}
          </div>
        )}

        <div className="bp-history">
          {historyRows.map((row, i) => (
            <div key={`${row.name}-${row.amount}-${i}`} className={`bp-hrow${i === 0 ? " newest" : ""}`}>
              <span className="nm">{row.name}</span>
              <span className="am">
                {fmt(row.amount)}
                <EspontIcon className="bp-hrow-icon" />
              </span>
            </div>
          ))}
        </div>

        {isSettled && (
          <div className="gavel-rig strike" key={`gavel-${settleKey}`}>
            <svg viewBox="0 0 120 150">
              <rect x="52" y="6" width="14" height="88" rx="6" fill="#8a5a30" />
              <rect x="18" y="86" width="82" height="34" rx="9" fill="#d49a63" />
              <rect x="18" y="86" width="82" height="10" rx="5" fill="rgba(255,255,255,.25)" />
              <rect x="10" y="80" width="14" height="46" rx="4" fill="#8a5a30" />
              <rect x="96" y="80" width="14" height="46" rx="4" fill="#8a5a30" />
            </svg>
          </div>
        )}

        {isSettled && (
          <div className="impact-wrap" key={`impact-${settleKey}`}>
            <div className="impact-flash" />
            <div className="impact-line" style={{ "--ang": "-70deg" } as CSSProperties} />
            <div className="impact-line" style={{ "--ang": "-35deg" } as CSSProperties} />
            <div className="impact-line" style={{ "--ang": "0deg" } as CSSProperties} />
            <div className="impact-line" style={{ "--ang": "35deg" } as CSSProperties} />
            <div className="impact-line" style={{ "--ang": "70deg" } as CSSProperties} />
          </div>
        )}

        <div className={`bp-winner${isSettled ? " shown" : ""}`}>
          <div className="bp-confetti">
            {isSettled &&
              confetti.map((c, i) => (
                <span
                  key={`${settleKey}-${i}`}
                  className={`confetto${c.gold ? " gold" : ""}`}
                  style={
                    {
                      "--x": `${c.x.toFixed(1)}px`,
                      "--y": `${c.y.toFixed(1)}px`,
                      "--r": `${c.r.toFixed(0)}deg`,
                      "--d": `${c.d.toFixed(0)}ms`,
                    } as CSSProperties
                  }
                />
              ))}
          </div>
          <div className="bp-winner-label">Auction closed &middot; winner</div>
          <div className="bp-winner-name">{state.winnerName ?? "–"}</div>
          <div className="bp-winner-amount">
            <span>{fmt(state.winnerAmount ?? 0)}</span>
            <EspontIcon className="bp-winner-amount-icon" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default BidAuction;

/**
 * A ticket-stílus (szaggatott perforáció, Archivo/Anton betűk) nem fejezhető
 * ki tisztán Tailwind-osztályokkal, ezért -- ugyanúgy mint a CardGame.tsx
 * saját CSS blokkja -- egy scoped <style> tag-ben él, hogy ne keveredjen az
 * overlay többi részének Tailwind-osztályaival. A betűtípus-import ide van
 * zárva, nem a globális index.css-be, hogy a widget önmagában hordozza a
 * függőségét.
 */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Anton&family=Archivo:wght@500;700;800;900&display=swap');

.bidauction-root {
  position: absolute;
  inset: 0;
  z-index: 50;
  border-radius: 24px;
  overflow: hidden;
  font-family: "Archivo", system-ui, sans-serif;
  animation: ba-fade-in .5s ease-out both;
}
.bidauction-root.bidauction-exit {
  animation: ba-exit .85s cubic-bezier(.76,0,.24,1) both;
  pointer-events: none;
}
@keyframes ba-fade-in { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: scale(1); } }
@keyframes ba-exit {
  from { opacity: 1; transform: translateX(0) rotate(0deg); filter: blur(0); }
  to { opacity: 0; transform: translateX(115%) rotate(2deg); filter: blur(8px); }
}

.bidauction-panel {
  position: absolute;
  inset: 0;
  border-radius: 24px;
  padding: 6% 8% 6%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #eef0f6;
  background: linear-gradient(150deg, #0d1518 0%, #0f2630 55%, #123844 100%);
  border: 2px solid rgba(142,0,255,.55);
}

.bp-label {
  display: flex; align-items: center; justify-content: space-between; gap: 7px;
  font-size: 12px; font-weight: 800; letter-spacing: .14em; text-transform: uppercase;
  color: #ffb199; margin-bottom: 8%;
}
.bp-label-left { display: flex; align-items: center; gap: 7px; min-width: 0; }
.bp-dot { width: 7px; height: 7px; border-radius: 50%; background: #ff5a36; animation: ba-pulse 1.4s ease-in-out infinite; flex: none; }
@keyframes ba-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .4; transform: scale(.7); } }
.bp-clock { font-variant-numeric: tabular-nums; color: #fff; font-weight: 900; flex: none; }
.bp-clock.danger { color: #ff5a5a; }

.bp-timebar-track {
  height: 4px; border-radius: 3px; margin-bottom: 7%; overflow: hidden;
  background: rgba(255,255,255,.12);
}
.bp-timebar-fill {
  height: 100%; border-radius: 3px;
  background: linear-gradient(90deg, #ff8a5c, #ff5a36);
  box-shadow: 0 0 10px rgba(255,90,54,.6);
  transition: width 1s linear, background .25s;
}
.bp-timebar-fill.danger { background: #ff2d2d; box-shadow: 0 0 10px rgba(255,45,45,.6); }

.bp-toprow {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin: 1% 0 5%; padding: 8% 7%; border-radius: 14px; background: rgba(0,0,0,.22);
}
.bp-toprow.pulse { animation: ba-bid-pulse 650ms ease-out; }
@keyframes ba-bid-pulse {
  0%   { background: rgba(255,90,54,.38); transform: scale(1); }
  30%  { background: rgba(255,90,54,.38); transform: scale(1.035); }
  100% { background: rgba(0,0,0,.22); transform: scale(1); }
}

.bp-amount {
  font-family: "Archivo", system-ui, sans-serif; font-weight: 900;
  font-size: 2.55em; line-height: .86; color: #fff;
  display: flex; align-items: baseline; gap: 8px; flex: none;
}
.bp-amount.pulse-num { animation: ba-num-pop 650ms cubic-bezier(.3,1.6,.4,1); }
@keyframes ba-num-pop { 0% { color: #fde047; } 40% { color: #fde047; } 100% { color: #fff; } }
.bp-amount-icon {
  width: .62em; height: .62em; filter: drop-shadow(0 0 10px rgba(253,224,71,.5));
  animation: ba-coin-float 2.6s ease-in-out infinite;
}
@keyframes ba-coin-float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-5px) rotate(8deg); }
}

.bp-bidder { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.bp-bidder-name {
  font-size: 1.18em; font-weight: 900; color: #fff; line-height: 1.2;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.bp-bidder-tag { font-size: .68em; font-weight: 800; text-transform: uppercase; letter-spacing: .12em; color: #9aa0b4; }

.bp-next {
  margin-top: 8%; padding-top: 6%; border-top: 2px solid rgba(255,140,90,.35);
  display: flex; align-items: center; justify-content: space-between; gap: 6% 10px;
  flex-wrap: wrap; row-gap: 6px;
  font-size: .95em; color: #eef0f6; font-weight: 800;
}
.bp-next b { color: #fde047; font-weight: 900; font-variant-numeric: tabular-nums; }
.bp-col { flex: 0 0 auto; min-width: 0; }
.bp-next .bp-col:last-child { text-align: right; }

.bp-next-amount { display: flex; align-items: center; gap: 4px; }
.bp-next-icon { width: 1em; height: 1em; flex: none; color: #ffb199; }

.bp-howto-wrap { flex: 1 1 auto; display: flex; justify-content: center; }
.bp-howto-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5% 7%; border-radius: 999px; white-space: nowrap;
  font-size: .78em; font-weight: 900; color: #fff;
  background: rgba(253,224,71,.12); border: 1.5px solid rgba(253,224,71,.5);
}
.bp-howto-badge b { color: #fde047; font-weight: 900; font-variant-numeric: tabular-nums; }
.bp-howto-icon { width: 1em; height: 1em; flex: none; color: #fde047; }

.bp-range {
  display: flex; align-items: center; justify-content: flex-end; gap: 5px;
  flex: 0 0 auto;
  font-size: .78em; color: #9aa0b4; font-weight: 700; font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.bp-range b { color: #fde047; }
.bp-range-icon { width: 1em; height: 1em; flex: none; color: #fde047; }

.bp-history { margin-top: 7%; flex: 1; min-height: 0; overflow: hidden; display: flex; flex-direction: column; gap: 7%; }
.bp-hrow {
  display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
  font-size: .82em; font-weight: 700; color: #9aa0b4;
  border-bottom: 1px dotted rgba(255,255,255,.14); padding-bottom: 5%;
}
.bp-hrow .nm { min-width: 0; overflow-wrap: anywhere; color: #eef0f6; }
.bp-hrow .am { flex: none; font-variant-numeric: tabular-nums; color: #9aa0b4; display: flex; align-items: center; gap: 4px; }
.bp-hrow-icon { width: 1em; height: 1em; animation: ba-coin-float 3.1s ease-in-out infinite; }
.bp-hrow.newest { color: #ffb199; animation: ba-rowdrop .4s ease-out; }
.bp-hrow.newest .nm { color: #fff; }
@keyframes ba-rowdrop { from { transform: translateY(-6px); opacity: 0; } to { transform: none; opacity: 1; } }

.bidauction-panel.bp-impact { animation: ba-shake .32s ease-out; }
@keyframes ba-shake {
  0% { transform: translate(0,0); }
  15% { transform: translate(-3px,2px) rotate(-.4deg); }
  30% { transform: translate(3px,-2px) rotate(.4deg); }
  45% { transform: translate(-2px,1px) rotate(-.2deg); }
  60% { transform: translate(2px,-1px) rotate(.2deg); }
  80% { transform: translate(-1px,0); }
  100% { transform: translate(0,0); }
}

.gavel-rig {
  position: absolute; top: 6%; right: 10%; width: 46%; height: 60%;
  transform-origin: 86% 8%; transform: rotate(-38deg); z-index: 4;
  pointer-events: none; filter: drop-shadow(0 6px 10px rgba(0,0,0,.5));
}
.gavel-rig.strike {
  animation: ba-gavel-swing 520ms cubic-bezier(.56,.04,.78,.3) forwards,
             ba-gavel-fade 320ms ease-in 480ms forwards;
}
@keyframes ba-gavel-swing {
  0%   { transform: rotate(-38deg); }
  78%  { transform: rotate(4deg); }
  88%  { transform: rotate(-3deg); }
  100% { transform: rotate(0deg); }
}
@keyframes ba-gavel-fade {
  from { opacity: 1; }
  to { opacity: 0; }
}
.gavel-rig svg { width: 100%; height: 100%; overflow: visible; }

.impact-wrap { position: absolute; inset: 0; z-index: 4; pointer-events: none; }
.impact-flash {
  position: absolute; left: 62%; top: 30%; width: 70px; height: 70px;
  transform: translate(-50%,-50%) scale(0); border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,.9) 0%, rgba(253,224,71,.55) 40%, transparent 72%);
  opacity: 0; animation: ba-flash-pop 380ms ease-out 430ms forwards;
}
@keyframes ba-flash-pop {
  0%   { opacity: 0; transform: translate(-50%,-50%) scale(0); }
  30%  { opacity: 1; transform: translate(-50%,-50%) scale(1.5); }
  100% { opacity: 0; transform: translate(-50%,-50%) scale(2.3); }
}
.impact-line {
  position: absolute; left: 62%; top: 30%; width: 3px; height: 22px;
  background: #fde047; border-radius: 2px; transform-origin: 50% 100%;
  opacity: 0; animation: ba-line-burst 360ms ease-out 430ms forwards;
}
@keyframes ba-line-burst {
  0%   { opacity: 0; transform: translate(-50%,-100%) rotate(var(--ang)) translateY(0) scaleY(.4); }
  35%  { opacity: 1; }
  100% { opacity: 0; transform: translate(-50%,-100%) rotate(var(--ang)) translateY(-26px) scaleY(1); }
}

.bp-winner {
  position: absolute; inset: 0; border-radius: inherit; z-index: 5;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px;
  text-align: center; padding: 0 6%;
  background: radial-gradient(ellipse 120% 90% at 50% 50%, rgba(255,90,54,.3), transparent 60%), rgba(10,6,8,.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  opacity: 0; transform: scale(.85); pointer-events: none;
}
.bp-winner.shown {
  opacity: 1; transform: scale(1); pointer-events: auto;
  animation: ba-winner-in .5s cubic-bezier(.34,1.56,.64,1) both;
}
@keyframes ba-winner-in { from { opacity: 0; transform: scale(.7); } 60% { opacity: 1; transform: scale(1.06); } to { opacity: 1; transform: scale(1); } }
.bp-winner-label { font-size: .85em; font-weight: 800; letter-spacing: .18em; text-transform: uppercase; color: #ffb199; }
.bp-winner-name { font-size: 1.65em; font-weight: 900; color: #fff; overflow-wrap: anywhere; line-height: 1.2; }
.bp-winner-amount {
  display: flex; align-items: baseline; gap: 8px; font-family: "Archivo", system-ui, sans-serif;
  font-weight: 900; font-size: 2.8em; color: #fde047;
}
.bp-winner-amount-icon { width: .6em; height: .6em; }

.bp-confetti { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
.confetto {
  position: absolute; left: 50%; top: 45%; width: 6px; height: 10px; border-radius: 1px;
  background: #ff5a36; transform: translate(-50%,-50%);
  animation: ba-confetti-pop .9s ease-out both;
  animation-delay: var(--d, 0ms);
}
.confetto.gold { background: #fde047; }
@keyframes ba-confetti-pop {
  0% { transform: translate(-50%,-50%) rotate(0deg) scale(0); opacity: 1; }
  70% { opacity: 1; }
  100% { transform: translate(calc(-50% + var(--x,0px)), calc(-50% + var(--y,0px))) rotate(var(--r,180deg)) scale(1); opacity: 0; }
}
`;
